import { KnzDiagnostic, KnzFrontmatter } from '../types';

/**
 * frontmatter 解析結果。
 * - frontmatter: 解析できたメタ情報（必須項目欠落時も部分的に返す）
 * - bodyStartLine: 本文が始まる行番号（1-indexed）。frontmatter 以降。
 * - diagnostics: 欠落・不正の診断
 */
export type FrontmatterResult = {
  frontmatter: KnzFrontmatter | null;
  bodyStartLine: number;
  diagnostics: KnzDiagnostic[];
};

/**
 * ファイル先頭の frontmatter（`---` で囲んだ限定 YAML）を解析する。
 *
 * - frontmatter は必須。先頭が `---` でない、または閉じ `---` がない場合は error。
 * - 必須項目は title / author。欠落は error（author は 1 件以上必須）。
 * - `key: value` 形式に加え、`author` は `author:` の下に `- id` を並べる複数指定にも対応する。
 * - `key: value` 形式のみ対応（ネスト・複雑な YAML は非対応）。
 */
/**
 * frontmatter 内のリスト項目行（`- id` / ` - id` / ` -id`）から id を取り出す。
 * 先頭のインデント（スペース/タブ）は 0 個以上を許容する（`author:` の下に非インデントで `- id` を並べる書き方にも対応）。
 * ハイフン直後の区切りスペース 1 つは除去する。
 */
const listItemValue = (line: string): string | null => {
  const m = line.match(/^\s*-(.*)$/);
  if (!m) return null;
  const rest = m[1];
  return rest.startsWith(' ') ? rest.slice(1).trim() : rest.trim();
};
export const parseFrontmatter = (lines: string[]): FrontmatterResult => {
  const diagnostics: KnzDiagnostic[] = [];

  // 先頭行が区切り `---` か
  if (lines.length === 0 || lines[0].trim() !== '---') {
    diagnostics.push({
      severity: 'error',
      message: 'frontmatter がありません。ファイル先頭を `---` で開始してください。',
      line: 1,
    });
    return { frontmatter: null, bodyStartLine: 0, diagnostics };
  }

  // 閉じ `---` を探す
  let closeIndex = -1;
  for (let i = 1; i < lines.length; i++) {
    if (lines[i].trim() === '---') {
      closeIndex = i;
      break;
    }
  }
  if (closeIndex === -1) {
    diagnostics.push({
      severity: 'error',
      message: 'frontmatter が閉じられていません（閉じの `---` がありません）。',
      line: 1,
    });
    return { frontmatter: null, bodyStartLine: lines.length, diagnostics };
  }

  // key: value を収集。`author` は複数指定（リスト形式）にも対応する。
  const map: Record<string, string> = {};
  const authors: string[] = [];
  for (let i = 1; i < closeIndex; i++) {
    const raw = lines[i];
    if (raw.trim() === '') continue;
    // リスト項目行（` - id`）は直前の key 行の処理でまとめて消費するため、単独では無視する
    if (listItemValue(raw) !== null) continue;
    const idx = raw.indexOf(':');
    if (idx === -1) {
      diagnostics.push({
        severity: 'warning',
        message: `frontmatter の行を解釈できません（\`key: value\` 形式で記述してください）: ${raw.trim()}`,
        line: i + 1,
      });
      continue;
    }
    const key = raw.slice(0, idx).trim();
    const value = raw.slice(idx + 1).trim();
    if (!key) continue;

    // author はリスト形式（`author:` の下に `- id`）と単一値（`author: id`）の両対応
    if (key === 'author') {
      if (value !== '') {
        // 単一値。カンマ区切りも許容する（`author: a, b`）。
        value
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s !== '')
          .forEach((s) => authors.push(s));
      } else {
        // 値が空 → 続くリスト項目行を著者として収集
        for (let j = i + 1; j < closeIndex; j++) {
          const item = listItemValue(lines[j]);
          if (item === null) break; // リスト項目でない行に達したら終了
          if (item !== '') authors.push(item);
        }
      }
      continue;
    }

    map[key] = value;
  }

  const bodyStartLine = closeIndex + 2; // 閉じ `---` の次行（1-indexed）

  // 必須チェック（author は 1 件以上必須）
  const title = map.title ?? '';
  const missing: string[] = [];
  if (!title) missing.push('title');
  if (authors.length === 0) missing.push('author');
  if (missing.length > 0) {
    diagnostics.push({
      severity: 'error',
      message: `frontmatter の必須項目が不足しています: ${missing.join(', ')}`,
      line: 1,
    });
    return { frontmatter: null, bodyStartLine, diagnostics };
  }

  const { title: _t, ...extra } = map;
  return {
    frontmatter: { title, authors, extra },
    bodyStartLine,
    diagnostics,
  };
};
